// @flow

/* eslint eslint-comments/no-unlimited-disable: warn */

import * as StringUtil from 'app/common/lib/StringUtil';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import Logging from 'app/common/lib/Logging';
import parseUri from 'app/packages/url/parseUri.external.js'; // eslint-disable-line import/extensions
import Pc from 'app/common/lib/Pc';
import punycodeDecode from 'shared/thirdparty/punycodeDecode';

const urlLog = Logging.getLogger('urlUtil');

const urlUtil = {};

/**
 * A sample link to an app in iTunes looks like:
 *     https://itunes.apple.com/us/app/wedpics-wedding-photo-app/id549402355?mt=8
 * or  https://itunes.apple.com/app/wedpics-wedding-photo-app/id549402355?mt=8
 * or  https://itunes.apple.com/app/id549402355?mt=8
 */
const iOSAppBaseUrlRegex = 'itunes\\.apple\\.com\\/(?:.*\\/)?app\\/(?:.*\\/)?id([0-9]+)';

/**
 * A sample link to a Google Play app looks like:
 *     https://play.google.com/store/apps/details?id=com.pinterest
 * or  https://play.google.com/store/apps/details?id=com.pinterest&hl=en
 * or  https://play.google.com/store/apps/details?hl=en&id=com.pinterest
 */
const androidAppBaseUrlRegex = 'play.google.com/store/apps/details';

/**
 * Parse a url string to return query string params.
 *
 * @param {string=} url A URI-encoded url string, uses
 *     window.location.search if null.
 * @return {Object.<string,string>} A mapping of key to value.  Keys and values
 *      are URI-decoded.
 */
urlUtil.getQueryStringParamsFromUrl = function(url?: string): Object {
  if (url) {
    const queryParts = url.split('?');
    if (queryParts[1]) {
      return urlUtil.getQueryStringParams(queryParts[1]);
    }
  }

  return urlUtil.getQueryStringParams();
};

/**
 * Parse a query string.
 *
 * @param {string=} query A URI-encoded query string without the "?", uses
 *     window.location.search if null.
 * @return {Object.<string,string>} A mapping of key to value.  Keys and values
 *      are URI-decoded.
 */
urlUtil.getQueryStringParams = function(query?: string): Object {
  if (query == null && typeof window !== 'undefined') {
    query = window.location.search;
  }

  const result = {};

  if (query) {
    // Strip the leading "?", if any.
    if (query[0] === '?') {
      query = query.substring(1);
    }
    // http://stackoverflow.com/questions/901115/how-can-i-get-query-string-values
    const pl = /\+/g; // Regex for replacing addition symbol with a space
    const search = /([^&=]+)=?([^&]*)/g;
    const decode = function(s) {
      let decodedComponent = s.replace(pl, ' ');
      try {
        decodedComponent = decodeURIComponent(decodedComponent);
      } catch (err) {
        // Attempt to handle poorly encoded URLs.
        //
        // The example case is https://app.asana.com/0/26468579792746/17760121512170.  In
        // this example, the web site owner has created the Save URL using Windows-1252
        // encoding, which is not a valid URL.  For example, the site uses %AE instead of
        // %C2%AE.
        //
        // This client-side piece is going to try to recover from bad web site content as
        // best it can, but this is only intended as best effort.
        //
        // NOTE(dcp): This does not fix the fact that the JS may still craft API calls based
        // on the browser bar.  That's handled separated in url_utils.py.
        //
        // NOTE(dcp): unescape is deprecated, but it's really the best function for this without
        // writing a whole decoding routine.
        declare function unescape(str: string): string;
        decodedComponent = unescape(decodedComponent);
      }
      return decodedComponent;
    };

    let match = search.exec(query);
    while (match) {
      const resultKey = decode(match[1]);
      // NOTE: there is a specific case for this key term_meta[] because there will be duplicate instances of this key.
      // ex: &term_meta[]=bird|term|1&term_meta[]=tweedy|term|2... so we need to assign an array to this key.
      if (resultKey === 'term_meta[]') {
        if (resultKey in result) {
          result[resultKey].push(decode(match[2]));
        } else {
          result[resultKey] = [decode(match[2])];
        }
      } else {
        result[resultKey] = decode(match[2]);
      }
      match = search.exec(query);
    }
  }

  return result;
};

/**
 * getQueryStringFromObject
 *
 * @param {Object.<string,(string|Array<string>>} A mapping of key to value.  Keys and values
 *      are URI-encoded
 *      If an value is a string, it will be URI encodeded in for form "key=value"
 *      If an value is an array of strings, it will be formatted in the form "key=[value1,value2]"
 *
 * @return {string=} query A URI-encoded query string without the "?", uses
 *     window.location.search if null.
 */
urlUtil.getQueryStringFromObject = function(obj: Object): string {
  // http://stackoverflow.com/questions/1714786/querystring-encoding-of-a-javascript-object
  const str = [];
  for (const p in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, p)) {
      const k = p,
        v = obj[p];
      if (v !== undefined) {
        let queryString;
        if (k === 'term_meta') {
          queryString = urlUtil.getQueryStringFromSearchTerms(v);
        } else if (Array.isArray(v)) {
          queryString =
            encodeURIComponent(k) + '=[' + v.map((val) => encodeURIComponent(val)).join(',') + ']';
        } else if (typeof v === 'object') {
          queryString = urlUtil.getQueryStringFromObject(v);
        } else {
          queryString = encodeURIComponent(k) + '=' + encodeURIComponent(v);
        }
        str.push(queryString);
      }
    }
  }
  return str.join('&');
};

urlUtil.getQueryStringFromSearchTerms = function(terms: Array<string>) {
  const str = [];
  terms.forEach((term) => {
    const queryString = 'term_meta[]=' + encodeURIComponent(term);
    str.push(queryString);
  });
  return str.join('&');
};

/**
 * This function is meant to be stubbed by test code.
 *
 * @private
 */
urlUtil._getAuthenticationOrigin = function() {
  return Pc.authenticationOrigin;
};

/**
 * This function is meant to be stubbed by test code.
 *
 * @private
 */
urlUtil._getTrustedHostnameEnding = function() {
  return Pc.trustedHostnameEnding;
};

/**
 * Create url that includes a "next" and "prev" query string param whose value is the URL
 * of the current page.
 *
 * @private
 * @param {string=} pathname The path without params
 * @param {Object.<string,string>=} params Query string params to be added to the redirect
 * @return {string} The register url.
 */
const _makeNextUrl = function(pathname, params) {
  params = params || {};

  if (params.next && urlLog) {
    urlLog.error('makeNextUrl: passing "next" in "params" is not allowed', { next: params.next });
  }

  const nextParams = Object.assign({}, params, {
    next: window.location.href,
  });

  if (!nextParams.prev) {
    nextParams.prev = window.location.href;
  }

  const nextUrl =
    urlUtil._getAuthenticationOrigin() +
    pathname +
    '?' +
    urlUtil.getQueryStringFromObject(nextParams);
  return nextUrl;
};

/** @type {string} */
let _entryUrl;

/**
 * Set the URL that user first navigated to.
 */
urlUtil.setEntryUrl = function(entryUrl: string) {
  _entryUrl = entryUrl;
};

/**
 * Set the URL that user first navigated to.
 * @return {string}
 */
urlUtil.getEntryUrl = function() {
  return _entryUrl;
};

/**
 * Get the register url.
 *
 * @param {Object.<string,string>=} params Query string params to be added to the redirect
 * @return {string} The register url.
 */
urlUtil.getRegisterUrl = function(params?: Object) {
  return _makeNextUrl('/join/', params);
};

/**
 * Get the login url.
 *
 * @param {Object.<string,string>=} params Query string params to be added to the redirect
 * @return {string} The login url.
 */
urlUtil.getLoginUrl = function(params?: Object) {
  return _makeNextUrl('/login/', params);
};

/**
 * Returns an absolute URL from a path. Uses the current location's
 * protocol and hostname.
 *
 * @param {string} path The path + query params
 * @param {string=} protocol e.g. http: (note the colon). Default is current location's.
 * @param {string=} hostnameAndPort e.g. "www.example.com", "www.example.com:123". Default is current location's.
 * @return {string} The absolute URL
 */
urlUtil.absolutify = function(path: string, protocol?: string, hostnameAndPort?: string) {
  if (path.match(/^https{0,1}:\/\//)) {
    return path;
  }
  if (protocol === undefined) {
    protocol = window.location.protocol;
  }
  if (hostnameAndPort === undefined) {
    hostnameAndPort = window.location.hostname;
    if (window.location.port) {
      hostnameAndPort += ':' + window.location.port;
    }
  }
  if (hostnameAndPort !== '' && path.charAt(0) !== '/') {
    path = '/' + path;
  }
  return protocol + '//' + hostnameAndPort + path;
};

/**
 * Make sure urls that are actually relative, act as if they are (IE sucks).
 * Example: "http://www.example.com/joshink/" -> "/joshink/".
 */
urlUtil.normalizeUrl = function(url: string): string {
  // See if the url is prefixed with our own protocol + host
  const currentUrl = urlUtil.absolutify('/');
  if (StringUtil.beginsWith(url, currentUrl)) {
    return url.substr(currentUrl.length - 1); // -1 to include the slash
  }

  // The url is from a different source.
  return url;
};

/**
 * Get the current URI
 * TODO(chris): Rename this to getCurrentPathAndQueryString.
 *
 * @return {string} The current URI
 */
urlUtil.getCurrentUrl = function(): string {
  return window.location.pathname + (window.location.search === '?' ? '' : window.location.search);
};

/**
 * Get path of current uri (ex. www.site.com/my/path/ => '/my/path/')
 *
 * @return {string} The current URI
 */
urlUtil.getCurrentPath = function(): string {
  return window.location.pathname;
};

/**
 * Is this a URL that should be opened through SafeLink?
 *
 * @param {string} url The url to check.
 * @return {boolean} true if url is truthy and contains an http or https scheme
              and is not from a trusted Pinterest domain.  Otherwise, false.
 */
urlUtil.isOffsiteUrl = function(url: string): boolean {
  return !!(url && url.match(/^https{0,1}:\/\//) && !urlUtil.isTrustedOrigin(url));
};

/**
 * Is this an App URL?
 *
 * @param {string} url The url to check.
 * @return {boolean} true if url is either a valid iOS or Android link.  Otherwise, false.
 */
urlUtil.isAppUrl = function(url: string): boolean {
  if (!url || url.length === 0) {
    return false;
  }
  const ios_regex = new RegExp(iOSAppBaseUrlRegex);
  const android_regex = new RegExp(androidAppBaseUrlRegex);
  return ios_regex.test(url) || android_regex.test(url);
};

/** @enum {number} */
urlUtil.UrlClass = {
  SAME_ORIGIN: 1, // This URL has the same origin as the page
  TRUSTED_DIFFERENT_ORIGIN: 2, // This URL has a different origin, but we trust its origin
  UNTRUSTED: 3, // This URL has a different origin that we don't trust
  INVALID: 4, // This value isn't a URL
};

/**
 * Get the origin of a URL.  If the supplied value isn't a URL
 * (e.g. "data:", "mail:", "javascript:") then return null.
 * Supported types include "http://", "https://", relative paths
 * will use current window.location values to populate
 *
 * @param {string} url
 * @param {boolean} noWindow if window is not available as a global
 * @return {{origin: ?string, hostname: ?string}}
 */
urlUtil.parse = function(url: string, noWindow?: boolean): Object {
  // Because this util is required by Node and browsers,
  // we can't rely on the DOM API A element to do the
  // parsing for us because we can't reference `document`
  let origin, hostname;
  const parsed = parseUri(url);

  // Make this URL absolute
  switch (parsed.protocol) {
    case 'http':
    case 'https':
      origin = parsed.protocol + '://' + parsed.authority;
      hostname = parsed.host;
      break;
    case '': // Protocol-less or Relative URL
      if (parsed.source.indexOf('//') === 0) {
        if (noWindow) {
          // If we don't have access to window.location, we can't
          // verify the url origin
          origin = null;
        } else {
          origin = window.location.protocol + '//' + parsed.authority;
        }
        hostname = parsed.host;
      } else if (noWindow) {
        // If we don't have access to window.location, we can't
        // return a relative url's origin or hostname
        origin = null;
        hostname = null;
      } else {
        origin = window.location.protocol + '//' + window.location.hostname;
        hostname = window.location.hostname;
      }
      break;
    default:
      // URL starts with a non-supported protocol
      origin = null;
      hostname = parsed.host;
  }

  return { origin, hostname };
};

/**
 * Classify a URL
 *
 * @param {string} url
 * @return {urlUtil.UrlClass}
 */
urlUtil.getUrlClass = function(url: string): number {
  const urlParsed = urlUtil.parse(url);
  if (urlParsed.origin === window.location.origin) {
    return urlUtil.UrlClass.SAME_ORIGIN;
  }

  if (urlParsed.hostname === window.location.hostname) {
    return urlUtil.UrlClass.TRUSTED_DIFFERENT_ORIGIN;
  }

  if (urlParsed.hostname) {
    const trustedEnding = urlUtil._getTrustedHostnameEnding();
    // trustedEnding will look like "foo.com".  We want to consider both "foo.com" and
    // "sub.foo.com" as trusted.
    if (
      urlParsed.hostname === trustedEnding ||
      StringUtil.endsWith(urlParsed.hostname || '', '.' + trustedEnding)
    ) {
      return urlUtil.UrlClass.TRUSTED_DIFFERENT_ORIGIN;
    }
  }

  const authOrigin = urlUtil._getAuthenticationOrigin();
  if (urlParsed.origin && urlParsed.origin === authOrigin) {
    return urlUtil.UrlClass.TRUSTED_DIFFERENT_ORIGIN;
  }

  if (urlParsed.origin) {
    return urlUtil.UrlClass.UNTRUSTED;
  }
  return urlUtil.UrlClass.INVALID;
};

/**
 * Returns true if the url is on a trusted Pinterest origin.
 */
urlUtil.isTrustedOrigin = function(url: string): boolean {
  switch (urlUtil.getUrlClass(url)) {
    case urlUtil.UrlClass.SAME_ORIGIN:
    case urlUtil.UrlClass.TRUSTED_DIFFERENT_ORIGIN:
      return true;
    default:
      return false;
  }
};

/**
 * Create a relative Url with the given path and params.
 * @param {string} pathname The relative path
 * @param {Object} params The params to be converted into a query string
 * @return {string} The relative url including the query string
 */
urlUtil.buildRelativeUrl = function(pathname: string, params: Object): string {
  const queryString = urlUtil.getQueryStringFromObject(params);
  return pathname + '?' + queryString;
};

/**
 * Append new values to an existing URL which may or may not already have a query string.
 *
 * @param {string} url
 * @param {Object} params The params to be appended to the query string
 * @return {string} url with appended query string values
 */
urlUtil.appendQueryString = function(url: string, params: Object): string {
  if (!isEmpty(params)) {
    // only append the url params that don't already exist on the url
    const pathQuery = url.split('?');
    const urlPath = pathQuery[0];
    const queryString = pathQuery[1];
    const existingUrlParams = queryString ? urlUtil.getQueryStringParams(queryString) : {};
    const uniqueParams = Object.assign({}, existingUrlParams, params);
    return urlPath + '?' + urlUtil.getQueryStringFromObject(uniqueParams);
  }

  return url;
};

/**
 * Remove query params from an existing URL.
 *
 * @param {string} url
 * @param {Array<string>} paramsToRemove The params to be removed
 * @return {string} url with edited query string values
 */
urlUtil.removeQueryParams = function(url: string, paramsToRemove: Array<string>): string {
  if (paramsToRemove && paramsToRemove.length > 0) {
    const pathQuery = url.split('?');
    const urlPath = pathQuery[0];
    const queryString = pathQuery[1];
    const params = queryString ? urlUtil.getQueryStringParams(queryString) : {};
    for (let i = 0; i < paramsToRemove.length; i += 1) {
      delete params[paramsToRemove[i]];
    }

    return (
      urlPath +
      (urlUtil.getQueryStringFromObject(params)
        ? '?' + urlUtil.getQueryStringFromObject(params)
        : '')
    );
  }

  return url;
};

/**
 * Check if experiment code exists in url param.
 *
 * @param {string} urlParam
 * @param {string} experimentCode The experiment code to check for
 * @return {boolean} true if urlParam already contains the experimentCode
 */
urlUtil.containsExperimentCode = function(urlParam: string, experimentCode: string): boolean {
  if (!urlParam) {
    return false;
  }

  // Check if experiment group exists
  const experiments = urlParam.split(' ');
  for (let i = 0; i < experiments.length; i += 1) {
    if (experiments[i].indexOf(experimentCode) > -1) {
      return true;
    }
  }
  return false;
};

/**
 * Update urlParam to include experimentCode
 *
 * @param {string} urlParam
 * @param {string} experimentGroupId The experiment group identifier to add
 * @return {string} urlParam with experimentGroupId added
 */
urlUtil.updateExperimentUrlParam = function(urlParam: string, experimentGroupId: string): string {
  if (!urlParam) {
    return experimentGroupId;
  }
  return urlParam + ' ' + experimentGroupId;
};

/**
 * Strip protocol and www, strip trailing slash from URLs
 */
urlUtil.canonicalURL = function(url: string): string {
  let u = url.toLowerCase();
  if (u.substring(0, 7) === 'http://') {
    u = u.substring(7);
  }
  if (u.substring(0, 8) === 'https://') {
    u = u.substring(8);
  }
  if (u.substring(0, 4) === 'www.') {
    u = u.substring(4);
  }
  if (u.substring(u.length - 1) === '/') {
    u = u.substring(0, u.length - 1);
  }
  return u;
};

/**
 * Get the reset one click url.
 *
 * @param {?Object} params Optional parameters.
 *
 * @return {string} The one click reset url.
 */
urlUtil.getResetPasswordOneClickUrl = function(params: Object): string {
  return '/login/reset/?' + urlUtil.getQueryStringFromObject(params);
};

/**
 * Convert an object into a more human readable representation.
 *
 * Example: {'name': 'Joshua', 'age': 27} -> (name='Joshua', age=28)
 *
 * @param {Object} args The object to convert.
 * @return {string} The readable string format.
 */
urlUtil.toScrubbedArgumentString = function(args: Object): string {
  const out = [];
  const sanitizeRegex = /(password|token)/i;
  forEach(args, (value, key) => {
    if (value && value.toString) {
      value = sanitizeRegex.test(key) ? undefined : value.toString();
    } else {
      value = JSON.stringify(value, (key, value) => {
        if (sanitizeRegex.test(key)) {
          return undefined;
        }
        return value;
      });
    }
    out.push(`${key}=${String(value)}`);
  });
  return out.join(', ');
};

urlUtil.punycode_decode = punycodeDecode;

urlUtil.prependSchemeIfNeeded = function(imageUrl: string, sourceUrl: ?string): string {
  if (!sourceUrl) {
    return imageUrl;
  }

  const regEx = new RegExp(/http(s?):/);
  if (!regEx.test(imageUrl)) {
    const match = regEx.exec(sourceUrl);
    if (match && match.length) {
      const scheme = match[0];
      imageUrl = scheme + imageUrl;
    }
  }
  return imageUrl;
};

/**
 * If a URL doesn't have a scheme provided (http(s?)), then add `http://`
 */
urlUtil.prependHttpSchemeIfNeeded = function(url: string): string {
  const regEx = new RegExp(/http(s?):/);
  return !regEx.test(url) ? 'http://' + url : url;
};

/**
 * Extract the unique page identifier from a URL, based on its page type.
 * Supported page types are klp, pin, board.
 * @param {string} pageType      Page type string. As found in UnauthContext.pageType.
 * @param {string} url           A URL to extract the page id from.
 * @returns {string}             The unique page identifier, or an error message.
 */
urlUtil.getUnauthPageId = function(pageType: string, url: string): string {
  let match;

  if (pageType === 'klp') {
    match = /.*explore\/([^?\/]+)/.exec(url);
  } else if (pageType === 'pin') {
    match = /.*pin\/([^?\/]+)/.exec(url);
  } else if (pageType === 'board') {
    match = /https?:\/\/[^\/]+\/([^?]+\/[^?\/]+)/.exec(url);
  } else {
    return 'UNKNOWN PAGE TYPE';
  }

  if (match && match.length > 1) {
    return match[1];
  } else {
    return 'ERROR FETCHING PAGE ID';
  }
};

urlUtil.validateUrl = function(url: string): boolean {
  const urlValidationRegex = /^(https?:\/\/)?[a-z0-9_\-]+\..+/i;

  if (url === '') {
    return true;
  }

  return urlValidationRegex.test(url);
};

export default urlUtil;
